import {
  createUserContentViewMutation,
  deleteUserContentViewMutation,
} from '@/client/services/api/graphql/mutations/bookmarks/bookmarkMutations';
import { useMutation, useQuery } from '@tanstack/react-query';

import { ContentTypes } from '@/client/services/api/graphql/gql/graphql';
import { graphQLClient } from '@/client/services/api/clients/graphqlClient';
import { listUserBookmarksQuery } from '@/client/services/api/graphql/queries/bookmarks/listUserBookmarksQuery';
import { useAuthStore } from '@/client/services/state/authStore';
import { useToastStore } from '@/client/services/state/toastStore';
import { useTranslation } from 'react-i18next';

export const useBookmarkActions = () => {
  const { t } = useTranslation();
  const { authConfig } = useAuthStore();
  const { user } = authConfig;
  const { _id: userId } = user;
  const { setToast } = useToastStore();

  const listUserBookmarks = () =>
    useQuery({
      queryKey: ['listUserBookmarks', userId],
      queryFn: async () => {
        const initialData = await graphQLClient.request(listUserBookmarksQuery, {
          user: { eq: userId },
        });

        const { totalCount } = initialData.listUserContentBookmarks.pageInfo;

        const allBookmarks = await graphQLClient.request(listUserBookmarksQuery, {
          user: { eq: userId },
          page: 0,
          pageSize: totalCount || 0,
        });

        return allBookmarks.listUserContentBookmarks;
      },
    });

  const { refetch } = listUserBookmarks();

  interface AddBookMarkMutationVariables {
    contentId: string;
    contentType: string;
  }

  const addBookmark = useMutation({
    mutationFn: async ({ contentId, contentType }: AddBookMarkMutationVariables) => {
      const contentTypeMap = {
        article: ContentTypes.Article,
        [ContentTypes.Article]: ContentTypes.Article,
        video: ContentTypes.Video,
        [ContentTypes.Video]: ContentTypes.Video,
        file: ContentTypes.File,
        [ContentTypes.File]: ContentTypes.File,
        imported_content: ContentTypes.ImportedContent,
        [ContentTypes.ImportedContent]: ContentTypes.ImportedContent,
        workshop: ContentTypes.Workshop,
        [ContentTypes.Workshop]: ContentTypes.Workshop,
        'multi-session-workshop': ContentTypes.MultiSessionWorkshop,
        [ContentTypes.MultiSessionWorkshop]: ContentTypes.MultiSessionWorkshop,
        track: ContentTypes.Track,
        [ContentTypes.Track]: ContentTypes.Track,
        journey: ContentTypes.Journey,
        [ContentTypes.Journey]: ContentTypes.Journey,
        scorm: ContentTypes.Scorm,
        [ContentTypes.Scorm]: ContentTypes.Scorm,
      };

      const data = await graphQLClient.request(createUserContentViewMutation, {
        userContentBookmark: {
          contentId,
          contentType: contentTypeMap[contentType as keyof typeof contentTypeMap],
        },
      });

      return data.createUserContentBookmark;
    },
    onSuccess: () => refetch(),
    onError: () =>
      setToast({
        show: true,
        status: 'error',
        title: t('bookmarks.add.error'),
      }),
  });

  const removeBookmark = useMutation({
    mutationFn: async (bookmarkId: string) => {
      const data = await graphQLClient.request(deleteUserContentViewMutation, {
        id: bookmarkId,
      });

      return data.deleteUserContentBookmark;
    },
    onSuccess: () => refetch(),
    onError: () =>
      setToast({
        show: true,
        status: 'error',
        title: t('bookmarks.remove.error'),
      }),
  });

  return { listUserBookmarks, addBookmark, removeBookmark };
};
